import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Plug,
  Settings,
  Link2,
  Plus,
  Pencil,
  BarChart3,
  ListTodo,
  Megaphone,
  Users,
  RefreshCw,
  CircleHelp,
  ExternalLink,
  X,
  Save,
  Loader2,
  List,
  UserRound,
  Building2,
  Unplug,
  Send,
  PlusCircle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import type { IntegrationConfig, WebhookEndpoint } from '@/types';
import { api } from '@/lib/api';
import { socialApi } from '@/lib/social-api';
import type { SocialPlatform, SocialAccount } from '@/lib/social-api';
import { PlatformIcon } from '@/components/social/PlatformIcon';
import { platformMeta } from '@/lib/social-platforms';
import { consumeHashOAuthFeedback } from '@/lib/hash-route';
import { oauthErrorMessage } from '@/lib/oauth-errors';

type IntegrationFieldMeta = {
  key: string;
  label: string;
  type: string;
  placeholder?: string;
  helpText?: string;
  tooltip?: string;
  example?: string;
  defaultValue?: string;
};

type IntegrationMeta = {
  type: IntegrationConfig['type'];
  label: string;
  docsUrl?: string;
  setupHints?: string[];
  testHint?: string;
  fields: IntegrationFieldMeta[];
};

const INTEGRATION_TYPES: IntegrationMeta[] = [
  {
    type: 'm365',
    label: 'Microsoft 365',
    docsUrl: 'https://learn.microsoft.com/de-de/entra/identity-platform/quickstart-register-app',
    setupHints: [
      'Azure App Registration erstellen und Redirect URI hinterlegen.',
      'API-Berechtigungen setzen und Admin Consent erteilen.',
      'Client Secret nur beim Erstellen kopieren und sicher speichern.',
    ],
    testHint: 'Test ist nur erfolgreich, wenn Tenant ID, Client ID und Client Secret gesetzt sind.',
    fields: [
      {
        key: 'tenant_id',
        label: 'Tenant ID',
        type: 'text',
        helpText: 'Zu finden in Microsoft Entra ID > Übersicht > Mandanten-ID.',
        tooltip: 'GUID der Azure/Microsoft-Mandanteninstanz.',
        example: 'Beispiel: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
      },
      {
        key: 'client_id',
        label: 'Client ID',
        type: 'text',
        helpText: 'In der App-Registrierung unter Anwendungs-ID (Client).',
        tooltip: 'Eindeutige ID der registrierten Anwendung.',
      },
      {
        key: 'client_secret',
        label: 'Client Secret',
        type: 'password',
        helpText: 'Unter Zertifikate & Geheimnisse > Neuer geheimer Clientschlüssel.',
        tooltip: 'Nur der Secret-Wert, nicht die Secret-ID.',
      },
    ],
  },
  {
    type: 'teams',
    label: 'Microsoft Teams',
    docsUrl: 'https://support.microsoft.com/de-de/office/create-an-incoming-webhook-in-teams-8ae491c7-0394-4861-ba59-055e33f75498',
    setupHints: [
      'Incoming Webhook im gewünschten Channel hinzufügen.',
      'Webhook-URL vollständig inklusive https:// eintragen.',
    ],
    testHint: 'Test prüft, ob eine Webhook-URL vorhanden ist.',
    fields: [
      {
        key: 'webhook_url',
        label: 'Webhook-URL',
        type: 'url',
        placeholder: 'https://...',
        helpText: 'In Teams im Kanal über Connectors / Workflows erzeugen.',
        tooltip: 'Muss eine öffentliche HTTPS-URL sein.',
      },
    ],
  },
  {
    type: 'mailchimp',
    label: 'Mailchimp',
    docsUrl: 'https://mailchimp.com/help/about-api-keys/',
    setupHints: [
      'API Key im Mailchimp-Profil unter Extras > API Keys erstellen.',
      'Server Prefix meist der Suffix hinter dem Bindestrich im API Key (z. B. us21).',
    ],
    testHint: 'Test benötigt api_key und server_prefix.',
    fields: [
      {
        key: 'api_key',
        label: 'API Key',
        type: 'password',
        helpText: 'Mailchimp Profil > Extras > API Keys.',
        tooltip: 'Geheim halten, nie in Tickets oder Chat posten.',
      },
      {
        key: 'server_prefix',
        label: 'Server (z. B. us1)',
        type: 'text',
        helpText: 'Prefix wie us1/us21; oft aus API-Key-Suffix ableitbar.',
        tooltip: 'Nur das Präfix ohne Punkte oder URL eingeben.',
      },
    ],
  },
  {
    type: 'google_ads',
    label: 'Google Ads',
    docsUrl: 'https://developers.google.com/google-ads/api/docs/oauth/overview',
    setupHints: [
      'OAuth-Client in Google Cloud Console erstellen (Webanwendung).',
      'Redirect URI in OAuth-Client und in der App identisch halten.',
      'Refresh Token mit dem passenden Google-Ads-Benutzer erzeugen.',
    ],
    testHint: 'Test benötigt client_id, client_secret und refresh_token.',
    fields: [
      {
        key: 'client_id',
        label: 'Client ID',
        type: 'text',
        helpText: 'Google Cloud Console > APIs & Dienste > Anmeldedaten.',
        tooltip: 'OAuth 2.0 Client-ID der Webanwendung.',
      },
      {
        key: 'client_secret',
        label: 'Client Secret',
        type: 'password',
        helpText: 'Zum gleichen OAuth-Client wie die Client ID.',
        tooltip: 'Geheim halten und regelmäßig rotieren.',
      },
      {
        key: 'refresh_token',
        label: 'Refresh Token',
        type: 'password',
        helpText: 'Über OAuth-Flow oder Playground erzeugen.',
        tooltip: 'Dient zur Token-Erneuerung ohne erneuten Login.',
      },
    ],
  },
  {
    type: 'linkedin',
    label: 'LinkedIn',
    docsUrl: 'https://learn.microsoft.com/de-de/linkedin/marketing/getting-started',
    setupHints: [
      'LinkedIn Developer App erstellen und Marketing-Produkt aktivieren.',
      'OAuth Redirect URLs exakt gleich in LinkedIn und in der App konfigurieren.',
      'Berechtigungen (Scopes) für Ads/Organisationen prüfen.',
    ],
    testHint: 'Test benötigt client_id und client_secret.',
    fields: [
      {
        key: 'client_id',
        label: 'Client ID',
        type: 'text',
        helpText: 'LinkedIn Developer Portal > App > Auth.',
        tooltip: 'Auch als Client Identifier in LinkedIn bezeichnet.',
      },
      {
        key: 'client_secret',
        label: 'Client Secret',
        type: 'password',
        helpText: 'LinkedIn Developer Portal > App > Auth > Client Secret.',
        tooltip: 'Secret-Wert sicher speichern, später nicht im Klartext teilen.',
      },
    ],
  },
  {
    type: 'lemlist',
    label: 'Lemlist',
    docsUrl: 'https://developer.lemlist.com/',
    setupHints: [
      'API Key im Lemlist-Account unter Integrationen/API erstellen.',
      'Bei Einschränkungen IP-Allowlist oder Workspace-Rechte prüfen.',
    ],
    testHint: 'Test benötigt api_key.',
    fields: [
      {
        key: 'api_key',
        label: 'API Key',
        type: 'password',
        helpText: 'Lemlist Einstellungen > Integrations/API.',
        tooltip: 'Mit minimal notwendigen Rechten erstellen.',
      },
    ],
  },
  {
    type: 'meta',
    label: 'Meta (Facebook / Instagram)',
    docsUrl: 'https://developers.facebook.com/docs/development/release',
    setupHints: [
      'Meta Developer App anlegen und Facebook Login / Instagram Graph API aktivieren.',
      'OAuth Redirect URL exakt wie META_OAUTH_REDIRECT_URL bzw. API-Callback setzen.',
      'Für Production: App Review (pages_manage_posts, instagram_content_publish u. a.) einreichen.',
    ],
    testHint: 'Test benötigt app_id und app_secret.',
    fields: [
      {
        key: 'app_id',
        label: 'App ID',
        type: 'text',
        helpText: 'Meta Developer Portal > App-Einstellungen > Basis.',
        tooltip: 'Öffentliche App-ID der Meta-Anwendung.',
      },
      {
        key: 'app_secret',
        label: 'App Secret',
        type: 'password',
        helpText: 'Meta Developer Portal > App-Einstellungen > Basis > App-Geheimnis anzeigen.',
        tooltip: 'Geheim halten; nur Server-seitig speichern.',
      },
      {
        key: 'graph_api_version',
        label: 'Graph API Version',
        type: 'text',
        defaultValue: 'v21.0',
        placeholder: 'v21.0',
        helpText:
          'Standard v21.0. Für Production-Features (Publishing) ist Meta App Review erforderlich – siehe docs/developer/META-APP-REVIEW.md.',
        tooltip: 'Version der Graph API, z. B. v21.0.',
      },
    ],
  },
  {
    type: 'youtube',
    label: 'YouTube',
    docsUrl: 'https://developers.google.com/youtube/v3/getting-started',
    setupHints: [
      'YouTube Data API v3 in der Google Cloud Console aktivieren.',
      'OAuth-Client hier anlegen ODER über GOOGLE_CLIENT_ID/GOOGLE_CLIENT_SECRET in der Server-Umgebung (gleicher Client wie Google SSO möglich).',
      'Redirect URI: YOUTUBE_OAUTH_REDIRECT_URL bzw. /api/social/oauth/youtube/callback.',
    ],
    testHint: 'Test prüft client_id/client_secret hier ODER GOOGLE_CLIENT_ID/GOOGLE_CLIENT_SECRET in der Umgebung.',
    fields: [
      {
        key: 'client_id',
        label: 'Client ID',
        type: 'text',
        helpText: 'Google Cloud Console > APIs > Credentials > OAuth 2.0 Client.',
        tooltip: 'Optional, wenn GOOGLE_CLIENT_ID in der Umgebung gesetzt ist.',
      },
      {
        key: 'client_secret',
        label: 'Client Secret',
        type: 'password',
        helpText: 'Google Cloud Console > Credentials > Client Secret.',
        tooltip: 'Optional, wenn GOOGLE_CLIENT_SECRET in der Umgebung gesetzt ist.',
      },
    ],
  },
  {
    type: 'tiktok',
    label: 'TikTok',
    docsUrl: 'https://developers.tiktok.com/doc/login-kit-web/',
    setupHints: [
      'TikTok Developer Console: App anlegen und Content Posting API freischalten.',
      'Scopes: user.info.basic, video.publish, video.upload.',
      'Redirect URI: TIKTOK_OAUTH_REDIRECT_URL bzw. /api/social/oauth/tiktok/callback.',
    ],
    testHint: 'Test benötigt Client Key und Client Secret.',
    fields: [
      {
        key: 'client_id',
        label: 'Client Key',
        type: 'text',
        helpText: 'TikTok Developers > App > Basic Info > Client Key.',
        tooltip: 'TikTok nennt die Client ID "Client Key".',
      },
      {
        key: 'client_secret',
        label: 'Client Secret',
        type: 'password',
        helpText: 'TikTok Developers > App > Basic Info > Client Secret.',
      },
    ],
  },
  {
    type: 'x',
    label: 'X (Twitter)',
    docsUrl: 'https://developer.x.com/en/docs/authentication/oauth-2-0/user-access-token',
    setupHints: [
      'X Developer Portal: Projekt & App anlegen, OAuth 2.0 mit PKCE aktivieren.',
      'App-Type auf "Web App" stellen, Redirect-URL exakt eintragen.',
      'Scopes: tweet.read, tweet.write, users.read, offline.access.',
      'Redirect URI: X_OAUTH_REDIRECT_URL bzw. /api/social/oauth/x/callback.',
    ],
    testHint: 'Test benötigt Client ID und Client Secret (OAuth 2.0 PKCE).',
    fields: [
      {
        key: 'client_id',
        label: 'Client ID',
        type: 'text',
        helpText: 'X Developer Portal > App > Keys and Tokens > OAuth 2.0 Client ID.',
      },
      {
        key: 'client_secret',
        label: 'Client Secret',
        type: 'password',
        helpText: 'X Developer Portal > App > Keys and Tokens > OAuth 2.0 Client Secret.',
      },
    ],
  },
  {
    type: 'threads',
    label: 'Threads',
    docsUrl: 'https://developers.facebook.com/docs/threads',
    setupHints: [
      'Meta Developer App anlegen und Threads API aktivieren.',
      'Scopes: threads_basic, threads_content_publish, threads_manage_insights.',
      'Redirect URI: THREADS_OAUTH_REDIRECT_URL bzw. /api/social/oauth/threads/callback.',
    ],
    testHint: 'Test benötigt App ID und App Secret.',
    fields: [
      {
        key: 'client_id',
        label: 'App ID',
        type: 'text',
        helpText: 'Meta Developer Portal > App-Einstellungen > Basis.',
      },
      {
        key: 'client_secret',
        label: 'App Secret',
        type: 'password',
        helpText: 'Meta Developer Portal > App-Einstellungen > App-Geheimnis.',
      },
    ],
  },
  {
    type: 'pinterest',
    label: 'Pinterest',
    docsUrl: 'https://developers.pinterest.com/docs/getting-started/introduction/',
    setupHints: [
      'Pinterest Developer Account anlegen und App erstellen (Sandbox optional).',
      'Scopes: boards:read, pins:read, pins:write, user_accounts:read.',
      'Redirect URI: PINTEREST_OAUTH_REDIRECT_URL bzw. /api/social/oauth/pinterest/callback.',
    ],
    testHint: 'Test benötigt App ID und App Secret.',
    fields: [
      {
        key: 'client_id',
        label: 'App ID',
        type: 'text',
        helpText: 'Pinterest Developers > App > App ID.',
      },
      {
        key: 'client_secret',
        label: 'App Secret',
        type: 'password',
        helpText: 'Pinterest Developers > App > App Secret.',
      },
    ],
  },
  {
    type: 'reddit',
    label: 'Reddit',
    docsUrl: 'https://www.reddit.com/dev/api/oauth/',
    setupHints: [
      'Reddit App anlegen (reddit.com/prefs/apps), Typ "web app".',
      'Scopes: identity, submit, read, flair, edit. Dauer: permanent (für Refresh-Token).',
      'Redirect URI: REDDIT_OAUTH_REDIRECT_URL bzw. /api/social/oauth/reddit/callback.',
      'REDDIT_USER_AGENT in der Umgebung eindeutig setzen (Reddit blockt generische Agents).',
    ],
    testHint: 'Test benötigt Client ID und Client Secret.',
    fields: [
      {
        key: 'client_id',
        label: 'Client ID',
        type: 'text',
        helpText: 'Reddit App-Einstellungen > unter dem App-Namen.',
      },
      {
        key: 'client_secret',
        label: 'Client Secret',
        type: 'password',
        helpText: 'Reddit App-Einstellungen > secret.',
      },
    ],
  },
  {
    type: 'google_business',
    label: 'Google Business Profile',
    docsUrl: 'https://developers.google.com/my-business/content/how-tos/local-posts',
    setupHints: [
      'Google Cloud: Google Business Profile API aktivieren + OAuth 2.0 Client.',
      'Scope: business.manage. Fällt auf den YouTube/Google-Client zurück, falls leer.',
      'Redirect URI: GOOGLE_BUSINESS_OAUTH_REDIRECT_URL bzw. /api/social/oauth/google_business/callback.',
    ],
    testHint: 'Test prüft client_id/client_secret hier ODER GOOGLE_CLIENT_ID/GOOGLE_CLIENT_SECRET.',
    fields: [
      {
        key: 'client_id',
        label: 'Client ID',
        type: 'text',
        helpText: 'Google Cloud Console > Credentials. Optional, wenn der Google-Client genutzt wird.',
      },
      {
        key: 'client_secret',
        label: 'Client Secret',
        type: 'password',
        helpText: 'Google Cloud Console > Credentials. Optional, wenn der Google-Client genutzt wird.',
      },
    ],
  },
  { type: 'webhook', label: 'Webhooks', fields: [] },
];
const INTEGRATIONS_HERO_IMAGE =
  'https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=1400&q=80';

/**
 * Mappt einen Integrations-Typ auf die Social-Plattform(en), die darüber
 * verbunden werden. `meta` deckt Facebook + Instagram gemeinsam ab.
 * Alle anderen Einträge sind null (kein Social-Kanal).
 */
const SOCIAL_PLATFORMS_FOR_TYPE: Partial<Record<IntegrationConfig['type'], SocialPlatform[]>> = {
  linkedin: ['linkedin'],
  meta: ['facebook', 'instagram'],
  youtube: ['youtube'],
  tiktok: ['tiktok'],
  x: ['x'],
  threads: ['threads'],
  pinterest: ['pinterest'],
  reddit: ['reddit'],
  google_business: ['google_business'],
};

/** Hash-Pfad, auf den der OAuth-Callback zurückkehren soll. */
const OAUTH_RETURN_TO = '/admin/integrations';

type ApiEnvelope<T> = {
  success: boolean;
  data?: T;
  error?: string;
};

type IntegrationDetailsResponse = IntegrationConfig & {
  config?: Record<string, unknown>;
  credentials?: Record<string, unknown>;
};

type CollectionResponse<T> = {
  data?: T[];
  total?: number;
  success?: boolean;
  error?: string;
};

type ActivityLite = {
  id: string;
  title: string;
  status: string;
};

type CampaignLite = {
  id: string;
  name: string;
  status: string;
};

type CostTrendPoint = {
  date: string;
  cost: number;
};

type PacingPoint = {
  target: number;
  actual: number;
  pacing_percent: number;
};

type IntegrationInsight = {
  loading: boolean;
  error: string | null;
  summary: Array<{ label: string; value: string }>;
  highlights: string[];
  updatedAt?: string;
};

type MailchimpInsightsPayload = {
  provider: 'mailchimp';
  available: boolean;
  message?: string;
  summary?: {
    audience_lists: number;
    audience_members: number;
    unsubscribed: number;
    cleaned: number;
    campaigns_total: number;
    campaigns_sent: number;
    avg_open_rate_percent: number;
    avg_click_rate_percent: number;
  };
  lists?: Array<{
    id: string;
    name: string;
    members: number;
    unsubscribed: number;
  }>;
  campaigns?: Array<{
    id: string;
    title: string;
    status: string;
    sent_at: string | null;
    emails_sent: number;
    open_rate_percent: number;
    click_rate_percent: number;
    unsubscribed: number;
    subscriber_clicks: number;
  }>;
};

const getApiError = (response: { error?: string } | undefined, fallback: string) =>
  response?.error?.trim() ? response.error : fallback;
const extractArray = <T,>(response: unknown): T[] => {
  const list = (response as CollectionResponse<T>).data;
  return Array.isArray(list) ? list : [];
};
const formatMaybeCurrency = (value: number) => (Number.isFinite(value) ? formatCurrency(value) : '-');

export function IntegrationsAdminPage() {
  const [integrations, setIntegrations] = useState<IntegrationConfig[]>([]);
  const [webhooks, setWebhooks] = useState<WebhookEndpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [configType, setConfigType] = useState<IntegrationConfig['type'] | null>(null);
  const [configIntegrationId, setConfigIntegrationId] = useState<string | null>(null);
  const [configForm, setConfigForm] = useState<Record<string, string>>({});
  const [storedCredentialValues, setStoredCredentialValues] = useState<Record<string, string>>({});
  const [configLoading, setConfigLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [webhookDialogOpen, setWebhookDialogOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<WebhookEndpoint | null>(null);
  const [webhookForm, setWebhookForm] = useState({ name: '', url: '', events: '' });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive' | 'error'>('all');
  const [syncingAdData, setSyncingAdData] = useState(false);
  const [insights, setInsights] = useState<Record<string, IntegrationInsight>>({});
  const [overview, setOverview] = useState({
    users: 0,
    campaigns: 0,
    activities: 0,
    loading: true,
  });
  const [webhookEventCatalog, setWebhookEventCatalog] = useState<Array<{ type: string; label_de: string }>>([]);
  const [providerConfigured, setProviderConfigured] = useState<Partial<Record<SocialPlatform, boolean>>>({});
  const [socialAccounts, setSocialAccounts] = useState<SocialAccount[]>([]);
  const [connectingPlatform, setConnectingPlatform] = useState<SocialPlatform | null>(null);
  const [busyAccountId, setBusyAccountId] = useState<string | null>(null);

  useEffect(() => {
    void api
      .get<ApiEnvelope<{ events: { type: string; label_de: string }[] }>>('/integrations/webhook-event-catalog')
      .then((res) => {
        if (res.success && Array.isArray(res.data?.events)) setWebhookEventCatalog(res.data.events);
      })
      .catch(() => setWebhookEventCatalog([]));
  }, []);

  // OAuth-Rückmeldung vom Callback einmalig anzeigen.
  useEffect(() => {
    const { error, connected } = consumeHashOAuthFeedback();
    if (error) toast.error(oauthErrorMessage(error), { id: `oauth-${error}` });
    else if (connected) toast.success('Konto verbunden.', { id: 'oauth-connected' });
  }, []);

  const filteredIntegrations = integrations.filter((integration) => {
    const matchesSearch = !search || integration.name.toLowerCase().includes(search.toLowerCase()) || integration.type.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || integration.status === statusFilter;
    return matchesSearch && matchesStatus;
  });


  const goTo = (path: string) => {
    window.location.hash = path.startsWith('/') ? path : `/${path}`;
  };

  const setInsightState = useCallback((type: IntegrationConfig['type'], next: IntegrationInsight) => {
    setInsights((prev) => ({ ...prev, [type]: next }));
  }, []);

  const loadBusinessOverview = useCallback(async () => {
    setOverview((prev) => ({ ...prev, loading: true }));
    try {
      const [usersRes, campaignsRes, activitiesRes] = await Promise.all([
        api.get<CollectionResponse<unknown>>('/users?limit=500').catch(() => ({ data: [] })),
        api.get<CollectionResponse<unknown>>('/campaigns?limit=500').catch(() => ({ data: [] })),
        api.get<CollectionResponse<unknown>>('/activities?limit=500').catch(() => ({ data: [] })),
      ]);
      setOverview({
        users: extractArray(usersRes).length,
        campaigns: extractArray(campaignsRes).length,
        activities: extractArray(activitiesRes).length,
        loading: false,
      });
    } catch {
      setOverview((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  /**
   * One-Click-Connect für jeden Social-Kanal. Holt die Authorize-URL vom API
   * und leitet den Browser dorthin weiter. Nach dem Consent kehrt der Callback
   * über `return_to` auf diese Seite zurück.
   */
  const connectSocial = async (
    platform: SocialPlatform,
    opts?: { type?: 'personal' | 'company' }
  ) => {
    if (providerConfigured[platform] === false) {
      toast.error(oauthErrorMessage('provider_not_configured'));
      return;
    }
    setConnectingPlatform(platform);
    try {
      const { url } = await socialApi.getAuthorizeUrl(platform, {
        type: opts?.type,
        returnTo: OAUTH_RETURN_TO,
      });
      window.location.href = url;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'OAuth konnte nicht gestartet werden.');
      setConnectingPlatform(null);
    }
  };

  const disconnectSocial = async (id: string) => {
    setBusyAccountId(id);
    try {
      await socialApi.disconnectAccount(id);
      setSocialAccounts((prev) => prev.filter((a) => a.id !== id));
      toast.success('Konto getrennt.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Trennen fehlgeschlagen.');
    } finally {
      setBusyAccountId(null);
    }
  };

  const reconnectSocial = async (account: SocialAccount) => {
    setBusyAccountId(account.id);
    try {
      await socialApi.refreshAccount(account.id);
      const fresh = await socialApi.listAccounts();
      setSocialAccounts(fresh);
      toast.success('Token aktualisiert.');
    } catch {
      // Refresh fehlgeschlagen → neu verbinden.
      void connectSocial(account.platform);
    } finally {
      setBusyAccountId(null);
    }
  };

  const loadData = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get<ApiEnvelope<IntegrationConfig[]>>('/integrations?limit=100'),
      api.get<ApiEnvelope<WebhookEndpoint[]>>('/integrations/webhooks?limit=100'),
      socialApi.listProviders().catch(() => null),
      socialApi.listAccounts().catch(() => null as SocialAccount[] | null),
    ])
      .then(([intRes, whRes, providers, accounts]) => {
        if (!intRes.success) {
          throw new Error(getApiError(intRes, 'Integrationen konnten nicht geladen werden.'));
        }
        if (!whRes.success) {
          throw new Error(getApiError(whRes, 'Webhooks konnten nicht geladen werden.'));
        }
        setIntegrations(intRes.data ?? []);
        setWebhooks(whRes.data ?? []);
        if (providers) {
          const map: Partial<Record<SocialPlatform, boolean>> = {};
          for (const p of providers) map[p.platform] = p.configured;
          setProviderConfigured(map);
        }
        if (accounts) setSocialAccounts(accounts);
      })
      .catch((loadError: unknown) => {
        setIntegrations([]);
        setWebhooks([]);
        setError((loadError as Error).message || 'Daten konnten nicht geladen werden.');
      })
      .finally(() => setLoading(false));
  }, []);

  const loadIntegrationInsight = useCallback(async (type: IntegrationConfig['type'], config?: IntegrationConfig) => {
    if (!config || config.status !== 'active') {
      setInsightState(type, {
        loading: false,
        error: null,
        summary: [],
        highlights: ['Insights verfügbar nach erfolgreichem Verbindungstest.'],
        updatedAt: new Date().toISOString(),
      });
      return;
    }

    setInsightState(type, { loading: true, error: null, summary: [], highlights: [] });
    try {
      if (type === 'mailchimp' || type === 'lemlist') {
        if (type === 'mailchimp' && config?.id) {
          const mailchimp = await api.get<ApiEnvelope<MailchimpInsightsPayload>>(`/integrations/${config.id}/insights`);
          const data = mailchimp.data;
          if (!mailchimp.success || !data) {
            throw new Error(getApiError(mailchimp, 'Mailchimp Insights konnten nicht geladen werden.'));
          }
          if (!data.available) {
            setInsightState(type, {
              loading: false,
              error: null,
              summary: [],
              highlights: [data.message || 'Mailchimp Live-Insights aktuell nicht verfügbar.'],
              updatedAt: new Date().toISOString(),
            });
            return;
          }
          setInsightState(type, {
            loading: false,
            error: null,
            summary: [
              { label: 'Audience Listen', value: String(data.summary?.audience_lists ?? 0) },
              { label: 'Kontakte', value: String(data.summary?.audience_members ?? 0) },
              { label: 'Gesendet', value: String(data.summary?.campaigns_sent ?? 0) },
              { label: 'Open/Click', value: `${(data.summary?.avg_open_rate_percent ?? 0).toFixed(1)}% / ${(data.summary?.avg_click_rate_percent ?? 0).toFixed(1)}%` },
            ],
            highlights: [
              ...(data.lists ?? []).slice(0, 2).map((item) => `Liste: ${item.name} (${item.members} Kontakte)`),
              ...(data.campaigns ?? []).slice(0, 2).map(
                (item) =>
                  `Campaign: ${item.title} | Sent: ${item.emails_sent} | Open: ${item.open_rate_percent.toFixed(1)}% | Click: ${item.click_rate_percent.toFixed(1)}%`
              ),
            ],
            updatedAt: new Date().toISOString(),
          });
          return;
        }

        const [campaignsRes, activitiesRes] = await Promise.all([
          api.get<CollectionResponse<CampaignLite>>('/campaigns?channel=email&limit=5'),
          api.get<CollectionResponse<ActivityLite>>('/activities?type=email&limit=5'),
        ]);
        const campaigns = extractArray<CampaignLite>(campaignsRes);
        const activities = extractArray<ActivityLite>(activitiesRes);
        setInsightState(type, {
          loading: false,
          error: null,
          summary: [
            { label: 'E-Mail Kampagnen', value: String(campaignsRes.total ?? campaigns.length) },
            { label: 'E-Mail Aktivitäten', value: String(activitiesRes.total ?? activities.length) },
          ],
          highlights: [
            ...campaigns.slice(0, 2).map((item) => `Kampagne: ${item.name} (${item.status})`),
            ...activities.slice(0, 2).map((item) => `Aktivität: ${item.title} (${item.status})`),
          ],
          updatedAt: new Date().toISOString(),
        });
        return;
      }

      if (type === 'google_ads' || type === 'linkedin') {
        const adPlatform = type === 'google_ads' ? 'google_ads' : 'linkedin_ads';
        const campaignChannel = type === 'google_ads' ? 'google' : 'linkedin';
        const activityType = type === 'google_ads' ? 'google_ads' : 'linkedin_ads';
        const currentYear = new Date().getFullYear();

        const [campaignsRes, activitiesRes, pacingRes, trendRes] = await Promise.all([
          api.get<CollectionResponse<CampaignLite>>(`/campaigns?channel=${campaignChannel}&limit=5`),
          api.get<CollectionResponse<ActivityLite>>(`/activities?type=${activityType}&limit=5`),
          api.get<CollectionResponse<PacingPoint>>(`/statistics/ad-pacing?year=${currentYear}&platform=${adPlatform}`),
          api.get<CollectionResponse<CostTrendPoint>>(`/statistics/ad-cost-trend?days=14&platform=${adPlatform}`),
        ]);

        const campaigns = extractArray<CampaignLite>(campaignsRes);
        const activities = extractArray<ActivityLite>(activitiesRes);
        const pacing = extractArray<PacingPoint>(pacingRes)[0];
        const trend = extractArray<CostTrendPoint>(trendRes);
        const spend14d = trend.reduce((sum, row) => sum + Number(row.cost || 0), 0);

        setInsightState(type, {
          loading: false,
          error: null,
          summary: [
            { label: 'Kampagnen', value: String(campaignsRes.total ?? campaigns.length) },
            { label: 'Aktivitäten', value: String(activitiesRes.total ?? activities.length) },
            { label: 'Spend 14 Tage', value: formatMaybeCurrency(spend14d) },
            { label: 'Pacing', value: pacing ? `${pacing.pacing_percent.toFixed(1)}%` : '-' },
          ],
          highlights: [
            ...campaigns.slice(0, 2).map((item) => `Kampagne: ${item.name} (${item.status})`),
            ...activities.slice(0, 2).map((item) => `Aktivität: ${item.title} (${item.status})`),
            pacing ? `Monatsziel: ${formatMaybeCurrency(pacing.target)} | Ist: ${formatMaybeCurrency(pacing.actual)}` : 'Noch keine Ad-Budget/Spend Daten vorhanden.',
          ],
          updatedAt: new Date().toISOString(),
        });
        return;
      }

      if (type === 'm365' || type === 'teams') {
        const [usersRes, webhooksRes, activitiesRes] = await Promise.all([
          api.get<CollectionResponse<unknown>>('/users?limit=500'),
          api.get<CollectionResponse<WebhookEndpoint>>('/integrations/webhooks?limit=100'),
          api.get<CollectionResponse<ActivityLite>>('/activities?limit=5'),
        ]);
        const activities = extractArray<ActivityLite>(activitiesRes);
        const webhooksData = extractArray<WebhookEndpoint>(webhooksRes);
        setInsightState(type, {
          loading: false,
          error: null,
          summary: [
            { label: 'Benutzer', value: String(usersRes.total ?? extractArray(usersRes).length) },
            { label: 'Webhooks', value: String(webhooksRes.total ?? webhooksData.length) },
            { label: 'Aktivitäten gesamt', value: String(activitiesRes.total ?? activities.length) },
          ],
          highlights: activities.slice(0, 3).map((item) => `Aktivität: ${item.title} (${item.status})`),
          updatedAt: new Date().toISOString(),
        });
        return;
      }

      setInsightState(type, {
        loading: false,
        error: null,
        summary: [{ label: 'Status', value: config.status }],
        highlights: ['Für diese Integration sind aktuell keine spezifischen Insights definiert.'],
        updatedAt: new Date().toISOString(),
      });
    } catch (insightError) {
      setInsightState(type, {
        loading: false,
        error: (insightError as Error).message || 'Insights konnten nicht geladen werden.',
        summary: [],
        highlights: [],
        updatedAt: new Date().toISOString(),
      });
    }
  }, [setInsightState]);

  useEffect(() => {
    loadData();
    loadBusinessOverview();
  }, [loadData, loadBusinessOverview]);

  useEffect(() => {
    INTEGRATION_TYPES.filter((item) => item.type !== 'webhook').forEach((item) => {
      const config = integrations.find((entry) => entry.type === item.type);
      void loadIntegrationInsight(item.type, config);
    });
  }, [integrations, webhooks.length, loadIntegrationInsight]);

  const openConfig = async (type: IntegrationConfig['type']) => {
    setConfigType(type);
    const existing = integrations.find((i) => i.type === type);
    setConfigIntegrationId(existing?.id ?? null);
    const meta = INTEGRATION_TYPES.find((m) => m.type === type);
    const initial: Record<string, string> = {};
    const existingSecretValues: Record<string, string> = {};
    meta?.fields.forEach((f) => {
      initial[f.key] = f.defaultValue ?? (f.type === 'checkbox' ? 'false' : '');
    });
    setConfigForm(initial);
    setStoredCredentialValues({});
    setConfigDialogOpen(true);

    if (!existing?.id) return;
    setConfigLoading(true);
    setError(null);
    try {
      const response = await api.get<ApiEnvelope<IntegrationDetailsResponse>>(`/integrations/${existing.id}`);
      if (!response.success || !response.data) {
        throw new Error(getApiError(response, 'Integration konnte nicht geladen werden.'));
      }

      const configValues: Record<string, unknown> = response.data.config ?? {};
      const credentialValues: Record<string, unknown> = response.data.credentials ?? {};

      const prefilled: Record<string, string> = {};
      meta?.fields.forEach((field) => {
        const rawValue = credentialValues[field.key] ?? configValues[field.key];
        if (field.type === 'password') {
          prefilled[field.key] = '';
          if (typeof rawValue === 'string' && rawValue.trim().length > 0 && !rawValue.includes('*')) {
            existingSecretValues[field.key] = rawValue;
          } else if (typeof rawValue === 'string' && rawValue.includes('*')) {
            existingSecretValues[field.key] = '__STORED__';
          }
          return;
        }
        if (field.type === 'checkbox') {
          const asBool =
            rawValue === true ||
            rawValue === 'true' ||
            rawValue === 1 ||
            rawValue === '1';
          prefilled[field.key] = asBool ? 'true' : field.defaultValue === 'true' && rawValue == null ? 'true' : 'false';
          return;
        }
        prefilled[field.key] =
          rawValue == null || rawValue === ''
            ? (field.defaultValue ?? '')
            : String(rawValue);
      });
      setConfigForm(prefilled);
      setStoredCredentialValues(existingSecretValues);
    } catch (configError) {
      setError((configError as Error).message || 'Konfiguration konnte nicht geladen werden.');
    } finally {
      setConfigLoading(false);
    }
  };

  const buildEncryptedPayload = () => {
    if (!configType) return { config_encrypted: '{}', credentials_encrypted: '{}' };
    const meta = INTEGRATION_TYPES.find((entry) => entry.type === configType);
    const configPayload: Record<string, string> = {};
    const credentialPayload: Record<string, string> = {};
    meta?.fields.forEach((field) => {
      const value = (configForm[field.key] ?? '').trim();
      if (field.type === 'password') {
        if (value.length > 0) {
          credentialPayload[field.key] = value;
        } else {
          const existingSecret = storedCredentialValues[field.key];
          if (existingSecret && existingSecret !== '__STORED__') {
            credentialPayload[field.key] = existingSecret;
          }
        }
      } else if (field.type === 'checkbox') {
        configPayload[field.key] = value === 'true' ? 'true' : 'false';
      } else if (value.length > 0) {
        configPayload[field.key] = value;
      }
    });
    return {
      config_encrypted: JSON.stringify(configPayload),
      credentials_encrypted: JSON.stringify(credentialPayload),
    };
  };

  const testConnection = async () => {
    if (!configType) return;
    setTesting(true);
    setError(null);
    try {
      let integrationId = configIntegrationId;
      const payload = buildEncryptedPayload();
      if (!integrationId) {
        const created = await api.post<ApiEnvelope<IntegrationConfig>>('/integrations', {
          type: configType,
          name: INTEGRATION_TYPES.find((i) => i.type === configType)?.label ?? configType,
          config_encrypted: payload.config_encrypted,
          credentials_encrypted: payload.credentials_encrypted,
          status: 'inactive',
        });
        if (!created.success || !created.data) {
          throw new Error(getApiError(created, 'Integration konnte nicht erstellt werden.'));
        }
        integrationId = created.data?.id ?? null;
        setConfigIntegrationId(integrationId);
      } else {
        const updated = await api.patch<ApiEnvelope<IntegrationConfig>>(`/integrations/${integrationId}`, {
          config_encrypted: payload.config_encrypted,
          credentials_encrypted: payload.credentials_encrypted,
        });
        if (!updated.success) {
          throw new Error(getApiError(updated, 'Integration konnte nicht aktualisiert werden.'));
        }
      }
      if (!integrationId) throw new Error('Integration konnte nicht vorbereitet werden.');
      const testResult = await api.post<ApiEnvelope<{ connected: boolean; message: string }>>(`/integrations/${integrationId}/test`);
      if (!testResult.success) {
        throw new Error(getApiError(testResult, 'Verbindungstest fehlgeschlagen.'));
      }
      loadData();
    } catch (e) {
      setError((e as Error).message ?? 'Verbindungstest fehlgeschlagen.');
    } finally {
      setTesting(false);
    }
  };

  const saveConfig = async () => {
    if (!configType) return;
    setError(null);
    try {
      const payload = buildEncryptedPayload();
      if (configIntegrationId) {
        const updated = await api.patch<ApiEnvelope<IntegrationConfig>>(`/integrations/${configIntegrationId}`, {
          config_encrypted: payload.config_encrypted,
          credentials_encrypted: payload.credentials_encrypted,
        });
        if (!updated.success) {
          throw new Error(getApiError(updated, 'Integration konnte nicht gespeichert werden.'));
        }
      } else {
        const created = await api.post<ApiEnvelope<IntegrationConfig>>('/integrations', {
          type: configType,
          name: INTEGRATION_TYPES.find((i) => i.type === configType)?.label ?? configType,
          config_encrypted: payload.config_encrypted,
          credentials_encrypted: payload.credentials_encrypted,
          status: 'inactive',
        });
        if (!created.success) {
          throw new Error(getApiError(created, 'Integration konnte nicht gespeichert werden.'));
        }
      }
      setConfigDialogOpen(false);
      loadData();
    } catch (e) {
      setError((e as Error).message ?? 'Speichern fehlgeschlagen.');
    }
  };

  const openWebhookEdit = (w?: WebhookEndpoint) => {
    setEditingWebhook(w ?? null);
    setWebhookForm(w ? { name: w.name, url: w.url, events: w.events.join(', ') } : { name: '', url: '', events: '' });
    setWebhookDialogOpen(true);
  };

  const saveWebhook = async () => {
    try {
      const payload = { name: webhookForm.name, url: webhookForm.url, events: webhookForm.events.split(',').map((e) => e.trim()).filter(Boolean) };
      if (editingWebhook) {
        const updated = await api.patch<ApiEnvelope<WebhookEndpoint>>(`/integrations/webhooks/${editingWebhook.id}`, payload);
        if (!updated.success) {
          throw new Error(getApiError(updated, 'Webhook konnte nicht gespeichert werden.'));
        }
      } else {
        const created = await api.post<ApiEnvelope<WebhookEndpoint>>('/integrations/webhooks', payload);
        if (!created.success) {
          throw new Error(getApiError(created, 'Webhook konnte nicht gespeichert werden.'));
        }
      }
      setWebhookDialogOpen(false);
      loadData();
    } catch (e) {
      setError((e as Error).message ?? 'Webhook konnte nicht gespeichert werden.');
    }
  };

  const triggerAdSync = async () => {
    try {
      setSyncingAdData(true);
      setError(null);
      await api.post<ApiEnvelope<{ queued: boolean }>>('/integrations/sync/ad-spend');
      loadData();
    } catch (e) {
      setError((e as Error).message || 'Ad-Spend-Sync konnte nicht gestartet werden.');
    } finally {
      setSyncingAdData(false);
    }
  };

  const getIntegrationActions = (type: IntegrationConfig['type']) => {
    if (type === 'google_ads') {
      return [
        { label: 'Diagramme', icon: BarChart3, onClick: () => goTo('/statistics') },
        { label: 'Google Kampagnen', icon: Megaphone, onClick: () => goTo('/campaigns?channel=google') },
        { label: 'Google Aktivitäten', icon: ListTodo, onClick: () => goTo('/activities?type=google_ads') },
        { label: syncingAdData ? 'Sync läuft…' : 'Ad-Daten sync', icon: RefreshCw, onClick: triggerAdSync, disabled: syncingAdData },
      ];
    }
    if (type === 'linkedin') {
      return [
        { label: 'LinkedIn Posts', icon: Send, onClick: () => goTo('/linkedin') },
        { label: 'Diagramme', icon: BarChart3, onClick: () => goTo('/statistics') },
        { label: 'LinkedIn Kampagnen', icon: Megaphone, onClick: () => goTo('/campaigns?channel=linkedin') },
        { label: 'LinkedIn Aktivitäten', icon: ListTodo, onClick: () => goTo('/activities?type=linkedin_ads') },
        { label: syncingAdData ? 'Sync läuft…' : 'Ad-Daten sync', icon: RefreshCw, onClick: triggerAdSync, disabled: syncingAdData },
      ];
    }
    if (type === 'mailchimp' || type === 'lemlist') {
      return [
        { label: 'E-Mail Kampagnen', icon: Megaphone, onClick: () => goTo('/campaigns?channel=email') },
        { label: 'E-Mail Aktivitäten', icon: ListTodo, onClick: () => goTo('/activities?type=email') },
        { label: 'Diagramme', icon: BarChart3, onClick: () => goTo('/statistics') },
      ];
    }
    if (type === 'm365' || type === 'teams') {
      return [
        { label: 'Aktivitäten', icon: ListTodo, onClick: () => goTo('/activities') },
        { label: 'Kampagnen', icon: Megaphone, onClick: () => goTo('/campaigns') },
        { label: 'Diagramme', icon: BarChart3, onClick: () => goTo('/statistics') },
      ];
    }
    return [
      { label: 'Kampagnen', icon: Megaphone, onClick: () => goTo('/campaigns') },
      { label: 'Aktivitäten', icon: ListTodo, onClick: () => goTo('/activities') },
      { label: 'Diagramme', icon: BarChart3, onClick: () => goTo('/statistics') },
    ];
  };

  /** True, wenn für diesen Integrations-Typ OAuth-App-Credentials (env oder DB)
   *  vorliegen — dann verbirgt sich hinter "Nicht konfiguriert" kein echter
   *  Arbeitsschritt mehr, und One-Click-Connect ist möglich. */
  const isSocialReady = (type: IntegrationConfig['type']): boolean => {
    const platforms = SOCIAL_PLATFORMS_FOR_TYPE[type];
    if (!platforms?.length) return false;
    return platforms.some((p) => providerConfigured[p] === true);
  };

  const trafficLight = (config: IntegrationConfig | undefined, type: IntegrationConfig['type']) => {
    // Keine DB-Zeile, aber App-Credentials vorhanden (z. B. zentral über Env).
    // Der Nutzer muss nichts mehr konfigurieren — nur noch verbinden.
    if (!config) {
      if (isSocialReady(type)) {
        return {
          label: 'Bereit',
          badgeClass: 'border-accent/30 bg-accent-soft text-accent',
          dotClass: 'bg-accent',
          textClass: 'text-accent',
        };
      }
      return {
        label: 'Nicht konfiguriert',
        badgeClass: 'border-border/70 bg-card-hover text-muted dark:border-border',
        dotClass: 'bg-muted',
        textClass: 'text-muted',
      };
    }
    if (config.status === 'active') {
      return {
        label: 'Verbunden',
        badgeClass: 'border-success/30 bg-success-soft text-success',
        dotClass: 'bg-success',
        textClass: 'text-success',
      };
    }
    if (config.status === 'error') {
      return {
        label: 'Fehler',
        badgeClass: 'border-danger/30 bg-danger-soft text-danger',
        dotClass: 'bg-danger',
        textClass: 'text-danger',
      };
    }
    return {
      label: 'Konfiguriert, ungetestet',
      badgeClass: 'border-warning/30 bg-warning-soft text-warning',
      dotClass: 'bg-warning',
      textClass: 'text-warning',
    };
  };

  const activeConfigMeta = configType ? INTEGRATION_TYPES.find((m) => m.type === configType) : null;

  if (loading && integrations.length === 0) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-border border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-sm dark:bg-background">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.14] dark:opacity-[0.1]"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.96), rgba(255,255,255,0.78)), url(${INTEGRATIONS_HERO_IMAGE})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="relative p-5 lg:p-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-accent-soft px-3 py-1 text-xs font-medium text-accent">
              <Plug className="h-3.5 w-3.5" />
              Connected Stack
            </div>
            <h2 className="mt-3 text-xl font-semibold text-foreground">
              Integrationen mit Live-Insights
            </h2>
            <p className="mt-1 text-sm text-muted">
              Verbinde Tools, teste Verbindungen und springe direkt in Kampagnen-, Aktivitäts- und KPI-Ansichten.
            </p>
          </div>
      </div>
      <div className="mb-4 max-w-4xl space-y-2">
        <h1 className="text-2xl font-bold text-foreground">Integrationen</h1>
      </div>

      <Card className="border-border bg-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Mehrwert & Übersicht</CardTitle>
          <p className="text-sm text-muted">
            Direkter Zugriff auf Kampagnen, Aktivitäten, Diagramme und Team-Übersicht.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-border p-3">
              <p className="text-xs text-muted">Benutzer</p>
              <p className="text-lg font-semibold text-foreground">{overview.loading ? '...' : overview.users}</p>
            </div>
            <div className="rounded-lg border border-border p-3">
              <p className="text-xs text-muted">Kampagnen</p>
              <p className="text-lg font-semibold text-foreground">{overview.loading ? '...' : overview.campaigns}</p>
            </div>
            <div className="rounded-lg border border-border p-3">
              <p className="text-xs text-muted">Aktivitäten</p>
              <p className="text-lg font-semibold text-foreground">{overview.loading ? '...' : overview.activities}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => goTo('/statistics')}>
              <BarChart3 className="mr-1 h-4 w-4" />
              Diagramme / KPI
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => goTo('/campaigns')}>
              <Megaphone className="mr-1 h-4 w-4" />
              Kampagnen
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => goTo('/activities')}>
              <ListTodo className="mr-1 h-4 w-4" />
              Aktivitäten
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => goTo('/admin/users')}>
              <Users className="mr-1 h-4 w-4" />
              Benutzer
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border bg-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <List className="h-5 w-5" />
            Webhook-Ereignisse (Katalog)
          </CardTitle>
          <p className="text-sm text-muted">
            Event-Typen für ausgehende Webhooks.
          </p>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[min(320px,50vh)] pr-3">
            <ul className="space-y-2 text-sm">
              {webhookEventCatalog.length === 0 ? (
                <li className="text-muted">Katalog wird geladen oder ist leer.</li>
              ) : (
                webhookEventCatalog.map((ev) => (
                  <li
                    key={ev.type}
                    className="flex flex-col gap-0.5 rounded-lg border border-border px-3 py-2 font-mono text-xs"
                  >
                    <span className="text-foreground">{ev.type}</span>
                    <span className="text-[11px] text-muted">{ev.label_de}</span>
                  </li>
                ))
              )}
            </ul>
          </ScrollArea>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-2">
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Integration suchen..."
          className="max-w-sm"
        />
        <select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value as 'all' | 'active' | 'inactive' | 'error')}
          className="rounded-md border border-border bg-card px-3 py-2 text-sm"
        >
          <option value="all">Alle Status</option>
          <option value="active">Aktiv</option>
          <option value="inactive">Inaktiv</option>
          <option value="error">Fehler</option>
        </select>
      </div>

      {error && (
        <div className="rounded-lg border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning">
          {error}
        </div>
      )}

      {(() => {
        const allMeta = INTEGRATION_TYPES.filter((i) => i.type !== 'webhook');
        const connected = allMeta.filter((m) => filteredIntegrations.find((c) => c.type === m.type)?.status === 'active');
        const errored = allMeta.filter((m) => filteredIntegrations.find((c) => c.type === m.type)?.status === 'error');
        // "Bereit": kein aktiver DB-Eintrag, aber App-Credentials vorhanden
        // (zentral über Env oder eigene Zeile) → One-Click-Connect möglich.
        const ready = allMeta.filter((m) => {
          const cfg = filteredIntegrations.find((c) => c.type === m.type);
          if (cfg?.status === 'active' || cfg?.status === 'error') return false;
          return isSocialReady(m.type);
        });
        const unconfigured = allMeta.filter((m) => {
          const cfg = filteredIntegrations.find((c) => c.type === m.type);
          if (cfg?.status === 'active' || cfg?.status === 'error') return false;
          if (isSocialReady(m.type)) return false; // → "Bereit"
          return true;
        });

        const accountsForType = (type: IntegrationConfig['type']): SocialAccount[] => {
          const platforms = SOCIAL_PLATFORMS_FOR_TYPE[type];
          if (!platforms?.length) return [];
          return socialAccounts.filter((a) => platforms.includes(a.platform));
        };

        const renderCard = (meta: IntegrationMeta) => {
          const config = filteredIntegrations.find((c) => c.type === meta.type);
          const state = trafficLight(config, meta.type);
          const insight = insights[meta.type];
          const platforms = SOCIAL_PLATFORMS_FOR_TYPE[meta.type] ?? [];
          const accounts = accountsForType(meta.type);
          const isSocial = platforms.length > 0;
          const readyToConnect = isSocial && platforms.some((p) => providerConfigured[p] !== false);
          return (
            <Card key={meta.type} className="border-border bg-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-card-hover text-foreground dark:bg-primary-hover dark:text-muted">
                      <Plug className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{meta.label}</CardTitle>
                      <p className="text-xs text-muted">
                        {config?.last_sync ? `Letzter Test: ${formatDate(config.last_sync, 'relative')}` : 'Noch nicht getestet'}
                      </p>
                    </div>
                  </div>
                  <div className={cn('inline-flex items-center gap-2 rounded-full border px-2 py-1 text-[11px] font-medium', state.badgeClass)}>
                    <span className={cn('h-2.5 w-2.5 rounded-full', state.dotClass)} />
                    {state.label}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                  {config?.error_message && <p className="mb-2 text-xs text-danger">{config.error_message}</p>}
                  {!config?.error_message && config && (
                    <p className={cn('mb-2 text-xs', state.textClass)}>Status: {state.label}</p>
                  )}
                  {!config && isSocial && readyToConnect && (
                    <p className="mb-2 text-xs text-muted">Zentral konfiguriert — einfach Konto verbinden.</p>
                  )}
                  {!config && !readyToConnect && <p className="mb-2 text-xs text-muted">Noch keine Konfiguration hinterlegt.</p>}
                {config?.status === 'active' && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {getIntegrationActions(meta.type).map((action) => {
                      const ActionIcon = action.icon;
                      return (
                        <Button
                          key={`${meta.type}-${action.label}`}
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={action.onClick}
                          disabled={action.disabled}
                          className="h-7 px-2 text-[11px]"
                        >
                          <ActionIcon className={cn('mr-1 h-3.5 w-3.5', action.disabled && 'animate-spin')} />
                          {action.label}
                        </Button>
                      );
                    })}
                  </div>
                )}

                {/* Verbundene Social-Konten + One-Click-Connect */}
                {isSocial && readyToConnect && (
                  <div className="mb-3 space-y-2">
                    {accounts.length > 0 && (
                      <ul className="space-y-1.5">
                        {accounts.map((acc) => (
                          <li key={acc.id} className="flex items-center justify-between rounded-md border border-border bg-background px-2.5 py-1.5 dark:bg-card-hover/40">
                            <div className="flex min-w-0 items-center gap-2">
                              <PlatformIcon platform={acc.platform} size={24} />
                              <div className="min-w-0">
                                <p className="truncate text-xs font-medium text-foreground">{acc.display_name || platformMeta(acc.platform).label}</p>
                                <p className="text-[10px] text-muted">
                                  <span className={acc.status === 'active' ? 'text-success' : 'text-danger'}>
                                    {acc.status === 'active' ? 'Aktiv' : acc.status === 'expired' ? 'Abgelaufen' : acc.status === 'revoked' ? 'Widerrufen' : 'Fehler'}
                                  </span>
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 px-1.5 text-[11px] text-muted"
                                disabled={busyAccountId === acc.id || connectingPlatform === acc.platform}
                                onClick={() => void reconnectSocial(acc)}
                              >
                                {busyAccountId === acc.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-7 px-1.5 text-[11px] text-muted hover:text-danger"
                                disabled={busyAccountId === acc.id}
                                onClick={() => void disconnectSocial(acc.id)}
                              >
                                {busyAccountId === acc.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Unplug className="h-3.5 w-3.5" />}
                              </Button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                    {platforms.length > 1 ? (
                      <div className="flex flex-wrap gap-2">
                        {platforms.map((p) => (
                          <Button
                            key={p}
                            type="button"
                            size="sm"
                            className="h-8 gap-1.5 text-xs"
                            disabled={connectingPlatform === p || providerConfigured[p] === false}
                            onClick={() => void connectSocial(p)}
                          >
                            {connectingPlatform === p ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <PlatformIcon platform={p} size={16} />
                            )}
                            {platformMeta(p).label} verbinden
                          </Button>
                        ))}
                      </div>
                    ) : platforms[0] === 'linkedin' ? (
                      // LinkedIn: Wahl zwischen persönlich / Company.
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 gap-1.5 text-xs"
                          disabled={connectingPlatform === 'linkedin'}
                          onClick={() => void connectSocial('linkedin', { type: 'personal' })}
                        >
                          {connectingPlatform === 'linkedin' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserRound className="h-3.5 w-3.5" />}
                          Persönlich verbinden
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 gap-1.5 text-xs"
                          disabled={connectingPlatform === 'linkedin'}
                          onClick={() => void connectSocial('linkedin', { type: 'company' })}
                        >
                          <Building2 className="h-3.5 w-3.5" />
                          Company verbinden
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        className="h-8 w-full gap-1.5 text-xs"
                        disabled={connectingPlatform === platforms[0]}
                        onClick={() => platforms[0] && void connectSocial(platforms[0])}
                      >
                        {connectingPlatform === platforms[0] ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <PlusCircle className="h-3.5 w-3.5" />
                        )}
                        Konto verbinden
                      </Button>
                    )}
                  </div>
                )}
                {isSocial && !readyToConnect && (
                  <div className="mb-3 rounded-md border border-dashed border-border bg-background px-3 py-2 text-[11px] text-muted dark:bg-card-hover/40">
                    Serverseitig noch nicht eingerichtet. App-Credentials siehe unten.
                  </div>
                )}

                <div className="mb-3 rounded-lg border border-border bg-background p-2.5 dark:bg-card-hover/40">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">Insights</p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-[11px]"
                      onClick={() => loadIntegrationInsight(meta.type, config)}
                    >
                      <RefreshCw className={cn('mr-1 h-3.5 w-3.5', insight?.loading && 'animate-spin')} />
                      Aktualisieren
                    </Button>
                  </div>
                  {insight?.loading && <p className="text-xs text-muted">Insights werden geladen...</p>}
                  {!insight?.loading && insight?.error && <p className="text-xs text-danger">{insight.error}</p>}
                  {!insight?.loading && !insight?.error && (
                    <>
                      {insight?.summary?.length ? (
                        <div className="mb-2 grid grid-cols-2 gap-2">
                          {insight.summary.map((entry) => (
                            <div key={`${meta.type}-${entry.label}`} className="rounded border border-border bg-card p-2">
                              <p className="text-[10px] uppercase text-muted">{entry.label}</p>
                              <p className="text-xs font-semibold text-foreground">{entry.value}</p>
                            </div>
                          ))}
                        </div>
                      ) : null}
                      {insight?.highlights?.length ? (
                        <div className="space-y-1">
                          {insight.highlights.slice(0, 3).map((item) => (
                            <p key={`${meta.type}-${item}`} className="text-xs text-muted">
                              • {item}
                            </p>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-muted">Noch keine Insights vorhanden.</p>
                      )}
                      {insight?.updatedAt && (
                        <p className="mt-2 text-[10px] text-muted">Aktualisiert: {formatDate(insight.updatedAt, 'relative')}</p>
                      )}
                    </>
                  )}
                </div>
                <Button variant="ghost" size="sm" className="w-full text-muted" onClick={() => openConfig(meta.type)}>
                  <Settings className="mr-2 h-4 w-4" />
                  Eigene App-Daten{isSocial && readyToConnect ? ' (optional)' : ''}
                </Button>
              </CardContent>
            </Card>
          );
        };

        return (
          <div className="space-y-6">
            {connected.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-success" />
                  <h3 className="text-sm font-semibold text-muted">Verbunden ({connected.length})</h3>                  <div className="flex-1 border-t border-border" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {connected.map(renderCard)}
                </div>
              </div>
            )}
            {errored.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-danger" />
                  <h3 className="text-sm font-semibold text-muted">Fehler ({errored.length})</h3>
                  <div className="flex-1 border-t border-border" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {errored.map(renderCard)}
                </div>
              </div>
            )}
            {ready.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-accent" />
                  <h3 className="text-sm font-semibold text-muted">Bereit ({ready.length})</h3>
                  <div className="flex-1 border-t border-border" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {ready.map(renderCard)}
                </div>
              </div>
            )}
            {unconfigured.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-muted" />
                  <h3 className="text-sm font-semibold text-muted">Nicht konfiguriert ({unconfigured.length})</h3>
                  <div className="flex-1 border-t border-border" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {unconfigured.map(renderCard)}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      <Card className="border-border bg-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Webhooks
            </CardTitle>
            <Button size="sm" onClick={() => openWebhookEdit()}>
              <Plus className="mr-2 h-4 w-4" />
              Webhook anlegen
            </Button>
          </div>
          <p className="text-sm text-muted">Ereignisse an externe URLs senden.</p>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[240px]">
            <ul className="space-y-2">
              {webhooks.length === 0 ? (
                <li className="py-6 text-center text-muted">Keine Webhooks konfiguriert.</li>
              ) : (
                webhooks.map((w) => (
                  <li key={w.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                    <div>
                      <p className="font-medium text-foreground">{w.name}</p>
                      <p className="text-xs text-muted truncate max-w-md">{w.url}</p>
                      <p className="text-xs text-muted">{w.events.join(', ')}</p>
                      {w.last_triggered && <p className="text-xs text-muted">Zuletzt: {formatDate(w.last_triggered, 'relative')}</p>}
                    </div>
                    <div className="flex gap-2">
                      <Badge variant={w.is_active ? 'success' : 'secondary'}>{w.is_active ? 'Aktiv' : 'Inaktiv'}</Badge>
                      <Button variant="ghost" size="icon" onClick={() => openWebhookEdit(w)}><Pencil className="h-4 w-4" /></Button>
                    </div>
                  </li>
                ))
              )}
            </ul>
          </ScrollArea>
        </CardContent>
      </Card>

      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="max-h-[min(90dvh,880px)] w-[calc(100vw-2rem)] min-w-0 overflow-x-hidden overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{activeConfigMeta?.label} konfigurieren</DialogTitle>
          </DialogHeader>
          <div className="grid min-w-0 gap-4 py-4">
            {activeConfigMeta?.setupHints?.length || activeConfigMeta?.docsUrl || activeConfigMeta?.testHint ? (
              <div className="min-w-0 break-words rounded-lg border border-border bg-background p-3 text-xs text-muted dark:bg-card/60">
                {activeConfigMeta?.docsUrl ? (
                  <a
                    href={activeConfigMeta.docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mb-2 inline-flex items-center gap-1 text-foreground hover:underline dark:text-muted"
                  >
                    Setup-Dokumentation öffnen
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                ) : null}
                {activeConfigMeta?.setupHints?.length ? (
                  <ul className="list-disc space-y-1 pl-4">
                    {activeConfigMeta.setupHints.map((hint) => (
                      <li key={`${activeConfigMeta.type}-${hint}`}>{hint}</li>
                    ))}
                  </ul>
                ) : null}
                {activeConfigMeta?.testHint ? (
                  <p className={cn('mt-2 border-t border-border pt-2', activeConfigMeta.setupHints?.length ? '' : 'mt-0 border-t-0 pt-0')}>
                    Test-Hinweis: {activeConfigMeta.testHint}
                  </p>
                ) : null}
              </div>
            ) : null}

            {activeConfigMeta?.fields.map((f) => (
              <div key={f.key} className="grid min-w-0 gap-1.5">
                <div className="flex min-w-0 items-center gap-1.5">
                  <Label>{f.label}</Label>
                  {f.tooltip ? (
                    <TooltipProvider delayDuration={100}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex shrink-0 text-muted hover:text-foreground dark:hover:text-foreground"
                          >
                            <CircleHelp className="h-3.5 w-3.5" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" align="start" className="max-w-[min(280px,calc(100vw-2rem))] text-xs">
                          {f.tooltip}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : null}
                </div>
                {f.type === 'checkbox' ? (
                  <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                    <span className="text-sm text-muted">Aktiviert</span>
                    <Switch
                      checked={(configForm[f.key] ?? f.defaultValue ?? 'false') === 'true'}
                      onCheckedChange={(v) => setConfigForm((prev) => ({ ...prev, [f.key]: v ? 'true' : 'false' }))}
                    />
                  </div>
                ) : (
                  <Input
                    className="min-w-0 font-mono text-xs sm:text-sm"
                    type={f.type as 'text'}
                    placeholder={
                      f.type === 'password' && storedCredentialValues[f.key]
                        ? 'Bereits gespeichert (leer lassen = beibehalten)'
                        : f.placeholder
                    }
                    value={configForm[f.key] ?? ''}
                    onChange={(e) => setConfigForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                  />
                )}
                {(f.helpText || f.example) ? (
                  <p className="text-[11px] text-muted">
                    {f.helpText}
                    {f.helpText && f.example ? ' ' : ''}
                    {f.example ? `${f.example}` : ''}
                  </p>
                ) : null}
              </div>
            ))}
            {configLoading && <p className="text-xs text-muted">Bestehende Konfiguration wird geladen...</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>
              <X className="h-4 w-4" />
              Abbrechen
            </Button>
            <Button variant="secondary" onClick={testConnection} disabled={testing || configLoading}>
              {testing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Teste…
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Verbindung testen
                </>
              )}
            </Button>
            <Button onClick={saveConfig} disabled={configLoading}>
              <Save className="h-4 w-4" />
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={webhookDialogOpen} onOpenChange={setWebhookDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingWebhook ? 'Webhook bearbeiten' : 'Neuer Webhook'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Name</Label>
              <Input value={webhookForm.name} onChange={(e) => setWebhookForm((f) => ({ ...f, name: e.target.value }))} placeholder="z. B. CRM Sync" />
              <p className="text-[11px] text-muted">Klarer Name für Zweck und Zielsystem, z. B. "CRM Kontakt-Sync".</p>
            </div>
            <div className="grid gap-2">
              <Label>URL</Label>
              <Input type="url" value={webhookForm.url} onChange={(e) => setWebhookForm((f) => ({ ...f, url: e.target.value }))} placeholder="https://..." />
              <p className="text-[11px] text-muted">Nur externe URL mit https:// verwenden. Interne/private Ziele werden aus Sicherheitsgründen blockiert.</p>
            </div>
            <div className="grid gap-2">
              <Label>Ereignisse (kommagetrennt)</Label>
              <Input value={webhookForm.events} onChange={(e) => setWebhookForm((f) => ({ ...f, events: e.target.value }))} placeholder="activity.updated, campaign.published" />
              <p className="text-[11px] text-muted">Mehrere Events per Komma trennen, z. B. <code>activity.updated, campaign.published</code>.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWebhookDialogOpen(false)}>
              <X className="h-4 w-4" />
              Abbrechen
            </Button>
            <Button onClick={saveWebhook}>
              <Save className="h-4 w-4" />
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
